import { Component, ErrorInfo, ReactNode } from 'react';
import { mayLoadDiagnostics } from '~/core/consent/choice';

interface ErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
  /** When this key changes, the error state resets. Use the current route path. */
  resetKey?: string;
  /** Called when the error state is reset (e.g., on navigation). */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown;
  /** Track the resetKey that was active when the error was captured */
  errorResetKey?: string;
  /** True if the boundary has ever caught an error and was just reset by a key change */
  didResetFromError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  readonly state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorResetKey: undefined,
    didResetFromError: false,
  };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    // If the resetKey changed since the error was captured, reset the error state.
    // This allows recovery on client-side navigation.
    if (
      state.hasError &&
      props.resetKey !== undefined &&
      state.errorResetKey !== undefined &&
      props.resetKey !== state.errorResetKey
    ) {
      return {
        hasError: false,
        error: null,
        errorResetKey: undefined,
        didResetFromError: true,
      };
    }
    return null;
  }

  componentDidUpdate(_prevProps: ErrorBoundaryProps) {
    // Call onReset only when transitioning from error to recovered
    // (not on every navigation -- didResetFromError is only set when
    // getDerivedStateFromProps clears an active error)
    if (this.state.didResetFromError && this.props.onReset) {
      // Clear the flag and notify
      this.setState({ didResetFromError: false });
      this.props.onReset();
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store the resetKey at the time of the error
    this.setState({ errorResetKey: this.props.resetKey });

    if (!mayLoadDiagnostics()) {
      console.error('ErrorBoundary caught error:', error, errorInfo);
      return;
    }

    // Report errors only after opt-in. Consent may change during the import.
    import('@sentry/browser')
      .then(({ captureException, withScope }) => {
        if (!mayLoadDiagnostics()) return;
        withScope((scope) => {
          scope.setExtra('componentStack', errorInfo.componentStack);
          scope.setTag('errorBoundary', 'AppErrorBoundary');
          captureException(error);
        });
      })
      .catch(() => {
        // Sentry not available, log to console as fallback
        console.error('ErrorBoundary caught error:', error, errorInfo);
      });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
